import React, { useState, useEffect } from 'react';
import { Calendar as CalendarIcon, Plus, Trash, User, CaretLeft as ChevronLeft, CaretRight as ChevronRight, FilePdf, Warning } from '@phosphor-icons/react';
import axios from '../../utils/axiosInstance';
import { useAuth } from '../../contexts/AuthContext';
import WniosekUrlopTab from './WniosekUrlopTab';
import SwietaSubotaTab from './SwietaSubotaTab';

const API = `${window.location.origin}/api`;

const MONTHS = [
  'Styczeń', 'Luty', 'Marzec', 'Kwiecień', 'Maj', 'Czerwiec',
  'Lipiec', 'Sierpień', 'Wrzesień', 'Październik', 'Listopad', 'Grudzień'
];

const DAYS_SHORT = ['Pon', 'Wt', 'Śr', 'Czw', 'Pt', 'Sob', 'Nie'];
const DAYS_MINI = ['P', 'W', 'Ś', 'C', 'P', 'S', 'N'];

export default function UrlowyTab() {
  const { user } = useAuth();
  const [subtab, setSubtab] = useState('kalendarz'); // 'kalendarz' | 'wniosek' | 'do-podpisu'
  const [urlopy, setUrlopy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  
  const isAdmin = user?.role === 'admin';
  
  // Get current month/year
  const currentMonth = currentDate.getMonth();
  const currentYear = currentDate.getFullYear();

  useEffect(() => {
    fetchUrlopy();
    if (isAdmin) {
      fetchEmployees();
    }
  }, [isAdmin]);

  const fetchEmployees = async () => {
    try {
      const response = await axios.get(`${API}/employees`);
      setEmployees(response.data);
    } catch (err) {
      console.error('Error fetching employees:', err);
    }
  };

  const fetchUrlopy = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/urlopy`);
      setUrlopy(response.data);
    } catch (err) {
      console.error('Error fetching urlopy:', err);
      alert('Błąd podczas pobierania danych urlopów');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUrlop = async (date) => {
    if (isAdmin && !selectedEmployeeId) {
      alert('Wybierz pracownika z listy przed dodaniem urlopu');
      return;
    }
    
    try {
      const payload = { date };
      if (isAdmin && selectedEmployeeId) {
        payload.employee_id = selectedEmployeeId;
      }
      await axios.post(`${API}/urlopy`, payload);
      fetchUrlopy();
    } catch (err) {
      console.error('Error adding urlop:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas dodawania urlopu';
      alert(errorMsg);
    }
  };

  const handleDeleteUrlop = async (urlopId) => {
    if (!window.confirm('Czy na pewno chcesz usunąć ten urlop?')) return;
    
    try {
      await axios.delete(`${API}/urlopy/${urlopId}`);
      fetchUrlopy();
    } catch (err) {
      console.error('Error deleting urlop:', err);
      const errorMsg = err.response?.data?.detail || 'Błąd podczas usuwania';
      alert(errorMsg);
    }
  };

  // Generate calendar days
  const getDaysInMonth = (year, month) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (year, month) => {
    const day = new Date(year, month, 1).getDay();
    return day === 0 ? 7 : day;
  };

  const generateCalendarDays = () => {
    const daysInMonth = getDaysInMonth(currentYear, currentMonth);
    const firstDay = getFirstDayOfMonth(currentYear, currentMonth);
    
    const days = [];
    for (let i = 1; i < firstDay; i++) {
      days.push(null);
    }
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(day);
    }
    
    return days;
  };

  const formatDate = (day) => {
    const month = String(currentMonth + 1).padStart(2, '0');
    const dayStr = String(day).padStart(2, '0');
    return `${currentYear}-${month}-${dayStr}`;
  };

  const getUrlopForDate = (day) => {
    if (!day) return null;
    const dateStr = formatDate(day);
    return urlopy.find(u => u.date === dateStr);
  };

  const isMyUrlop = (urlop) => {
    if (!urlop) return false;
    if (isAdmin && selectedEmployeeId) {
      return urlop.employee_id === selectedEmployeeId;
    }
    return urlop.employee_id === user._id;
  };

  const goToPreviousMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentDate(new Date(currentYear, currentMonth + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const calendarDays = generateCalendarDays();

  if (loading) {
    return <div className="text-center py-12">Ładowanie kalendarza...</div>;
  }

  return (
    <div className="max-w-[1400px] mx-auto px-3 py-4 sm:px-6 sm:py-6 space-y-4 sm:space-y-6" data-testid="urlopy-container">
      {/* Header */}
      <div className="flex items-center justify-between mb-2 sm:mb-6">
        <div>
          <h2 className="text-xl sm:text-3xl font-bold text-slate-800">Urlopy</h2>
          <p className="text-xs sm:text-base text-slate-600 mt-1">Wspólny kalendarz urlopów dla wszystkich pracowników</p>
        </div>
      </div>

      {/* Subtabs */}
      <div className="flex items-center gap-2 border-b border-border overflow-x-auto" data-testid="urlopy-subtabs">
        <button
          onClick={() => setSubtab('kalendarz')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
            subtab === 'kalendarz' ? 'border-[#2563EB] text-[#2563EB]' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="subtab-kalendarz"
        >
          <CalendarIcon className="w-4 h-4 inline mr-1.5" /> Kalendarz
        </button>
        <button
          onClick={() => setSubtab('wniosek')}
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
            subtab === 'wniosek' ? 'border-[#2563EB] text-[#2563EB]' : 'border-transparent text-muted-foreground hover:text-foreground'
          }`}
          data-testid="subtab-wniosek"
        >
          <FilePdf className="w-4 h-4 inline mr-1.5" /> {isAdmin ? 'Wnioski' : 'Wniosek o urlop'}
        </button>
        {isAdmin && (
          <button
            onClick={() => setSubtab('do-podpisu')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              subtab === 'do-podpisu' ? 'border-[#2563EB] text-[#2563EB]' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="subtab-do-podpisu"
          >
            <FilePdf className="w-4 h-4 inline mr-1.5" /> Do podpisu
          </button>
        )}
        {isAdmin && (
          <button
            onClick={() => setSubtab('swieta-sobota')}
            className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${
              subtab === 'swieta-sobota' ? 'border-[#2563EB] text-[#2563EB]' : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
            data-testid="subtab-swieta-sobota"
          >
            <Warning className="w-4 h-4 inline mr-1.5" /> Święta w sobotę
          </button>
        )}
      </div>

      {/* Content: Wniosek/Do-podpisu shortcuts */}
      {subtab === 'wniosek' && <WniosekUrlopTab mode="employee" />}
      {subtab === 'do-podpisu' && <WniosekUrlopTab mode="do-podpisu" />}
      {subtab === 'swieta-sobota' && <SwietaSubotaTab user={user} />}
      {subtab !== 'kalendarz' ? null : (<></>)}

      {/* KALENDARZ CONTENT (only when subtab === 'kalendarz') */}
      {subtab === 'kalendarz' && (<>
      {/* Admin: Employee Selector */}
      {isAdmin && (
        <div className="bg-muted rounded-xl p-3 sm:p-6 shadow-md border border-purple-200">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <div className="flex items-center gap-2 shrink-0">
              <User className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" weight="bold" />
              <label className="text-xs sm:text-sm font-bold text-purple-900">
                Wybierz pracownika:
              </label>
            </div>
            <select
              value={selectedEmployeeId || ''}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              className="flex-1 w-full px-3 py-2 sm:px-4 sm:py-3 border-2 border-purple-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent font-semibold text-sm sm:text-base"
              data-testid="urlopy-admin-employee-select"
            >
              <option value="">-- Wybierz pracownika --</option>
              {employees.map((emp) => (
                <option key={emp._id} value={emp._id}>
                  {emp.name}
                </option>
              ))}
            </select>
            {selectedEmployeeId && (
              <button
                onClick={() => setSelectedEmployeeId(null)}
                className="px-4 py-2 sm:py-3 bg-signal hover:bg-signal/90 text-white font-semibold transition-colors text-sm sm:text-base shrink-0"
                data-testid="urlopy-admin-clear-employee"
              >
                Wyczyść
              </button>
            )}
          </div>
          {selectedEmployeeId && (
            <div className="mt-2 sm:mt-3 text-xs sm:text-sm text-purple-700 font-medium break-words">
              ✓ Dodajesz urlop dla: <strong>{employees.find(e => e._id === selectedEmployeeId)?.name}</strong>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-xl p-3 sm:p-4 shadow-md border border-gray-200 grid grid-cols-1 sm:flex sm:flex-wrap gap-2 sm:gap-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-green-100 border-2 border-green-500 rounded shrink-0"></div>
          <span className="text-xs sm:text-sm font-medium text-slate-700">Dzień wolny (możesz wybrać)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-muted border-2 border-foreground rounded shrink-0"></div>
          <span className="text-xs sm:text-sm font-medium text-slate-700">Twój urlop</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 sm:w-6 sm:h-6 bg-red-100 border-2 border-red-400 rounded opacity-60 shrink-0"></div>
          <span className="text-xs sm:text-sm font-medium text-slate-700">Zajęte (zablokowane)</span>
        </div>
      </div>

      {/* Calendar Navigation */}
      <div className="bg-foreground rounded-xl sm:rounded-sm p-3 sm:p-6 text-white shadow-lg">
        <div className="flex items-center justify-between gap-2">
          <button
            onClick={goToPreviousMonth}
            className="p-2 sm:p-3 hover:bg-white/20 rounded-lg transition-colors flex items-center justify-center shrink-0"
            aria-label="Poprzedni miesiąc"
            data-testid="urlopy-prev-month"
          >
            <ChevronLeft className="w-5 h-5 sm:w-7 sm:h-7" weight="bold" />
          </button>
          
          <div className="text-center flex-1 min-w-0">
            <h3 className="text-lg sm:text-3xl font-bold truncate">
              {MONTHS[currentMonth]} {currentYear}
            </h3>
            <button
              onClick={goToToday}
              className="mt-1 sm:mt-2 px-3 py-1 bg-white/20 hover:bg-white/30 rounded-lg text-xs sm:text-sm font-semibold transition-colors"
              data-testid="urlopy-today-btn"
            >
              Dzisiaj
            </button>
          </div>
          
          <button
            onClick={goToNextMonth}
            className="p-2 sm:p-3 hover:bg-white/20 rounded-lg transition-colors flex items-center justify-center shrink-0"
            aria-label="Następny miesiąc"
            data-testid="urlopy-next-month"
          >
            <ChevronRight className="w-5 h-5 sm:w-7 sm:h-7" weight="bold" />
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="bg-white rounded-xl sm:rounded-sm shadow-lg border border-gray-100 overflow-hidden">
        {/* Day headers */}
        <div className="grid grid-cols-7 bg-muted border-b border-gray-200">
          {DAYS_SHORT.map((day, idx) => (
            <div key={day} className="text-center py-2 sm:py-3 font-bold text-slate-600 text-xs sm:text-sm">
              <span className="hidden sm:inline">{day}</span>
              <span className="sm:hidden">{DAYS_MINI[idx]}</span>
            </div>
          ))}
        </div>

        {/* Calendar days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, idx) => {
            if (!day) {
              return <div key={`empty-${idx}`} className="aspect-square border border-gray-100"></div>;
            }

            const urlop = getUrlopForDate(day);
            const isMine = isMyUrlop(urlop);
            const isOccupied = urlop && !isMine;
            const isFree = !urlop;

            return (
              <div
                key={day}
                className={`aspect-square border border-border p-1 sm:p-2 transition-all ${
                  isFree ? 'bg-white hover:bg-muted cursor-pointer' :
                  isMine ? 'bg-foreground hover:bg-black cursor-pointer' :
                  'bg-signal/10 opacity-70 cursor-not-allowed'
                }`}
                onClick={() => {
                  if (isFree) {
                    handleAddUrlop(formatDate(day));
                  } else if (isMine) {
                    handleDeleteUrlop(urlop.id);
                  }
                }}
                data-testid={`urlopy-day-${day}`}
              >
                <div className="h-full flex flex-col">
                  <div className={`text-xs sm:text-lg font-mono font-medium mb-0.5 sm:mb-1 leading-none ${
                    isMine ? 'text-background' : isOccupied ? 'text-signal' : 'text-foreground'
                  }`}>
                    {day}
                  </div>
                  
                  {urlop && (
                    <div className="flex-1 flex flex-col justify-center items-center min-h-0 w-full">
                      <div className={`hidden sm:flex w-8 h-8 items-center justify-center mb-1 shrink-0 ${
                        isMine ? 'bg-background text-foreground' : 'bg-signal text-white'
                      }`}>
                        <User className="w-5 h-5" weight="bold" />
                      </div>
                      <div
                        className={`text-[10px] sm:text-xs font-mono font-bold text-center uppercase tracking-tight sm:tracking-wider w-full leading-tight px-0.5 break-all ${
                          isMine ? 'text-background' : 'text-signal'
                        }`}
                        data-testid={`urlop-code-${urlop.id}`}
                      >
                        {urlop.employee_login || urlop.employee_name || '—'}
                      </div>
                    </div>
                  )}
                  
                  {isFree && (
                    <div className="flex-1 flex items-center justify-center">
                      <Plus className="w-4 h-4 sm:w-6 sm:h-6 text-muted-foreground opacity-40" weight="bold" />
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Statistics */}
      <div className="grid grid-cols-3 gap-2 sm:gap-6">
        <div className="bg-muted rounded-xl sm:rounded-sm p-3 sm:p-6 text-white shadow-lg">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <CalendarIcon className="w-5 h-5 sm:w-8 sm:h-8 shrink-0" weight="bold" />
            <h3 className="text-xs sm:text-lg font-semibold leading-tight">Wolne dni</h3>
          </div>
          <p className="text-2xl sm:text-4xl font-bold">
            {getDaysInMonth(currentYear, currentMonth) - urlopy.filter(u => {
              const date = new Date(u.date);
              return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
            }).length}
          </p>
        </div>
        
        <div className="bg-foreground rounded-xl sm:rounded-sm p-3 sm:p-6 text-white shadow-lg">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <User className="w-5 h-5 sm:w-8 sm:h-8 shrink-0" weight="bold" />
            <h3 className="text-xs sm:text-lg font-semibold leading-tight">Twoje urlopy</h3>
          </div>
          <p className="text-2xl sm:text-4xl font-bold">
            {urlopy.filter(u => {
              const date = new Date(u.date);
              return u.employee_id === user._id && 
                     date.getMonth() === currentMonth && 
                     date.getFullYear() === currentYear;
            }).length}
          </p>
        </div>
        
        <div className="bg-signal rounded-xl sm:rounded-sm p-3 sm:p-6 text-white shadow-lg">
          <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
            <CalendarIcon className="w-5 h-5 sm:w-8 sm:h-8 shrink-0" weight="bold" />
            <h3 className="text-xs sm:text-lg font-semibold leading-tight">Zajęte dni</h3>
          </div>
          <p className="text-2xl sm:text-4xl font-bold">
            {urlopy.filter(u => {
              const date = new Date(u.date);
              return date.getMonth() === currentMonth && date.getFullYear() === currentYear;
            }).length}
          </p>
        </div>
      </div>
      </>)}
    </div>
  );
}
